import React from "react";
//#region redux
import { useSelector } from "react-redux";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { DimensionTypeItem } from "models/propTypes/dimensionPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { DimensionInputSelectorProps } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import MessageBubble from "../../messages/messageBubble";
import FilterInput from "./filterInput";
//#endregion
import { DimensionHelper, LocalizationHelperInstance } from "utils/helpers";

function DimensionInputSelector(props: DimensionInputSelectorProps) {
  const { value, disabled, type, allowedTypes } = props;

  const { dimensionTypes, dimensionSuggestions, dimensions } = useSelector(
    DimensionHelper.getAppStateDimensionSelector(type)
  );

  const dimensionMap: DimensionTypeItem<AutocompleteListItem>[] = React.useMemo(() => {
    const selectedDimensionIds = props.value ?? [];

    const allowedDimensionTypes = allowedTypes?.length
      ? dimensionTypes.filter((d) => allowedTypes.includes(d.type))
      : dimensionTypes;

    const dimensionsMap = allowedDimensionTypes.map((companyDimType): DimensionTypeItem<AutocompleteListItem> => {
      const all = dimensions.find((d) => d.key.id === companyDimType.id);
      const suggestions = dimensionSuggestions.find((ds) => ds.key.id === companyDimType.id)?.value ?? [];
      const type = all?.key ?? companyDimType;

      const selected = all && all.value.find((d) => d.id && selectedDimensionIds.includes(d.id));

      return {
        id: type.id ?? "",
        name: type.name ?? "",
        type: type.type,
        suggestions: suggestions.map((dim): AutocompleteListItem => ({ id: dim.id, value: dim.name ?? "" })),
        selectedDimension: selected ? { id: selected.id, value: selected.name ?? "" } : undefined,
        all:
          all?.value.map(
            (dim): AutocompleteListItem => ({
              id: dim.id,
              value: dim.name ?? "",
              group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Other),
            })
          ) ?? [],
      };
    });
    return dimensionsMap;
  }, [dimensionTypes, dimensions, dimensionSuggestions, value]);

  const setValue = React.useCallback(
    (dimensionTypeId: string, selectedDimension: string | number | undefined) => {
      const dimensionsNew = [...dimensionMap];
      const dimension = dimensionsNew.find((x) => x.id === dimensionTypeId);
      if (dimension) {
        dimension.selectedDimension = dimension && dimension.all.find((d) => d.id === selectedDimension);

        onChange(dimensionsNew);
      }
    },
    [dimensionMap]
  );

  const onChange = (dimensionsUpdated: DimensionTypeItem<AutocompleteListItem>[]) => {
    props.setValue(
      dimensionsUpdated
        .filter((x) => x.selectedDimension !== undefined)
        .flatMap((dim) => dim.selectedDimension?.id) as string[],
      true
    );
  };

  return (
    <Grid container spacing={1}>
      <MessageBubble
        message={props.error}
        type={props.hasError ? StyleEnums.DisplayType.Danger : StyleEnums.DisplayType.Info}
        placement="top"
      ></MessageBubble>

      {dimensionMap.map((dimension: DimensionTypeItem<AutocompleteListItem>, index) => {
        const label = DimensionHelper.getDimensionTypeText(dimension.type, dimension.name);
        return (
          <Grid key={dimension.id} item xs={6}>
            <FilterInput
              name={props.name}
              label={label}
              placeholder={label}
              setValue={setValue.bind(null, dimension.id)}
              value={dimension.selectedDimension?.id}
              items={dimension.all}
              suggestedItems={dimension.suggestions}
              disabled={disabled}
              onBlur={props.onBlur}
              onChange={() => {}}
              setTouched={() => {}}
              touched={false}
              hasError={props.hasError && (typeof props.error !== "string" || props.error.search(label) !== -1)}
              optional={!dimensionTypes.find((d) => d.id === dimension.id)?.isForced}
              data-testid={`${props.name}DimensionInputSelector`}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}

export default DimensionInputSelector;
